import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FilterCondition } from '../../../common/classes/Filter/FilterCondition';
import { Filter } from '../../../common/classes/Filter/Filter';
import { Column } from '../../../common/interfaces';
import { AsyncStatus } from '../../../common/enums';
import { LanguageElements } from '../../../language-elements/LanguageElements';
import { reportColumnOptionsSelector } from '../Report/Selectors';
import FilterConditionComponent from './FilterCondition';
import { FilterConditionUI, FilterUI } from './types';

interface FilterConditionsProps {
    conditions: FilterConditionUI[];
    fields: Column[];
    showErrors: boolean;
    isFilterEmpty: boolean;
    activeCondition: number;
    disableDeleteCondition?: boolean;
    languageElements: LanguageElements;
    filter: FilterUI;
    onFilterUpdate: (filter: FilterUI) => void;
    onActiveConditionUpdate: (conditionIndex: number) => void;
    onShowErrorsUpdate: (showErrors: boolean) => void;
}

const FilterConditions: React.FC<FilterConditionsProps> = ({
    conditions,
    fields,
    showErrors,
    isFilterEmpty,
    activeCondition,
    disableDeleteCondition,
    languageElements,
    filter,
    onFilterUpdate,
    onActiveConditionUpdate,
    onShowErrorsUpdate,
}) => {
    const reportColumnOptions = useSelector(reportColumnOptionsSelector);

    const reportColumnOptionsData = useMemo(() => {
        if (reportColumnOptions.status === AsyncStatus.DONE) {
            return reportColumnOptions.data;
        }
        return null;
    }, [reportColumnOptions]);
    const handleUpdateFilterCondition = useCallback(
        (condition: FilterCondition, conditionIndex: number): void => {
            const updatedFilter = new Filter(fields, filter, false);
            updatedFilter.updateCondition(condition, conditionIndex);
            onActiveConditionUpdate(conditionIndex);
            onFilterUpdate(updatedFilter.toDisplayModel());
            onShowErrorsUpdate(false);
        },
        [fields, filter, onFilterUpdate, onActiveConditionUpdate, onShowErrorsUpdate]
    );

    const handleDeleteFilterCondition = useCallback(
        (conditionIndex: number): void => {
            const updatedFilter = new Filter(fields, filter, false);
            updatedFilter.deleteCondition(conditionIndex);
            onActiveConditionUpdate(0);
            onFilterUpdate(updatedFilter.toDisplayModel());
        },
        [fields, filter, onFilterUpdate, onActiveConditionUpdate]
    );

    const updateActiveConditionIndex = useCallback(
        (conditionIndex: number): void => {
            if (activeCondition !== conditionIndex) {
                onActiveConditionUpdate(conditionIndex);
            }
        },
        [activeCondition, onActiveConditionUpdate]
    );

    return (
        <>
            {conditions?.map((condition: FilterConditionUI, i: number) => (
                <FilterConditionComponent
                    key={i}
                    condition={condition}
                    fields={fields}
                    index={i}
                    showErrors={showErrors && !isFilterEmpty}
                    disableDeleteCondition={disableDeleteCondition ?? false}
                    activeCondition={activeCondition}
                    languageElements={languageElements}
                    onUpdateFilterCondition={handleUpdateFilterCondition}
                    onDeleteFilterCondition={handleDeleteFilterCondition}
                    updateActiveConditionIndex={updateActiveConditionIndex}
                    reportColumnOptions={reportColumnOptionsData}
                />
            ))}
        </>
    );
};

export default FilterConditions;
